import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Users, ExternalLink, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
};

export const MyPartnersCard = ({ authHeader }) => {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState("");

  const load = useCallback(async (m) => {
    try {
      const { data: d } = await axios.get(`${API}/mlo/partners${m ? `?month=${m}` : ""}`, { headers: authHeader() });
      setData(d);
      if (!m) setMonth(d.month);
    } catch {
      setData({ partners: [] });
    }
  }, [authHeader]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (!data || (data.partners || []).length === 0) return null;

  const categories = data.categories || {};
  const partners = data.partners || [];
  const activeCount = partners.filter((p) => p.total > 0).length;

  return (
    <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-6" data-testid="mlo-partners-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
            <Users size={13} /> My partners
          </div>
          <h3 className="mt-1 font-heading font-medium text-xl text-navy">What your BDMs did this month</h3>
          <p className="mt-1 text-sm text-ink-500">
            Straight from their own activity logs — {activeCount} of {partners.length} logged something in{" "}
            {monthLabel(month)}.
          </p>
        </div>
        <select
          value={month}
          onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
          data-testid="mlo-partners-month"
          className="rounded-full border border-linen-border bg-white px-3 py-1.5 text-xs text-navy focus:outline-none focus:ring-2 focus:ring-teal/40 shrink-0"
        >
          {(data.months || [month]).map((m) => (
            <option key={m} value={m}>{monthLabel(m)}</option>
          ))}
        </select>
      </div>

      <div className="mt-4 space-y-3">
        {partners.map((p) => (
          <div
            key={p.id}
            className="rounded-2xl border border-linen-border p-4"
            data-testid={`mlo-partner-${p.id}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-navy">
                  {p.name}
                  {p.company ? <span className="font-normal text-ink-400"> · {p.company}</span> : null}
                </p>
                <p className="text-[11px] text-ink-400 mt-0.5 inline-flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {p.last_login_at
                    ? `Last opened their dashboard ${new Date(p.last_login_at).toLocaleDateString()}`
                    : "Hasn't opened their dashboard yet"}
                </p>
              </div>
              <span
                className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md shrink-0 ${
                  p.total > 0 ? "bg-lime/25 text-navy" : "bg-amber-100 text-amber-800 border border-amber-300"
                }`}
              >
                {p.total > 0 ? `${p.total} logged` : "Nothing logged"}
              </span>
            </div>

            {(p.onboarding || !p.nexa_registered || p.workspace) && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`mlo-partner-onboarding-${p.id}`}>
                {p.workspace && (
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md ${
                      p.workspace.touches_this_week >= p.workspace.weekly_goal
                        ? "bg-teal-light text-teal"
                        : "bg-linen text-ink-600"
                    }`}
                  >
                    Week {p.workspace.touches_this_week}/{p.workspace.weekly_goal}
                  </span>
                )}
                {p.workspace?.streak > 0 && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-lime/25 text-navy">
                    {p.workspace.streak} week streak
                  </span>
                )}
                {p.journey && !p.journey.finished && (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-linen text-ink-600">
                    Day {p.journey.step}/{p.journey.total}
                  </span>
                )}
                {p.onboarding && (
                  <span
                    className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md ${
                      p.onboarding.percent === 100 ? "bg-teal-light text-teal" : "bg-linen text-ink-600"
                    }`}
                  >
                    First week {p.onboarding.done}/{p.onboarding.total}
                  </span>
                )}
                {!p.nexa_registered && (
                  <span
                    title="They can't be paid until they confirm their NEXA registration."
                    className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200"
                  >
                    NEXA not confirmed
                  </span>
                )}
              </div>
            )}

            {p.workspace?.next_move && (
              <p className="mt-2 text-[11px] text-ink-400" data-testid={`mlo-partner-next-${p.id}`}>
                Next move on their dashboard: {p.workspace.next_move}
              </p>
            )}

            {p.total > 0 ? (
              <>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {Object.entries(categories)
                    .filter(([key]) => (p.counts || {})[key] > 0)
                    .map(([key, label]) => (
                      <span key={key} className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-md bg-linen text-ink-600">
                        {label} · {p.counts[key]}
                      </span>
                    ))}
                </div>
                <ul className="mt-3 space-y-1">
                  {p.recent.map((r, i) => (
                    <li key={i} className="text-xs text-ink-500">
                      {r.occurred_on} — {r.title}
                      {r.url ? (
                        <a href={r.url} target="_blank" rel="noreferrer" className="ml-1 text-teal hover:underline inline-flex items-center gap-0.5">
                          view <ExternalLink className="w-3 h-3" />
                        </a>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="mt-2 text-xs text-ink-500">
                Worth a nudge — an empty log at month end means a thin compliance filing.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyPartnersCard;
